import { useEffect, useState, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { PlusIcon, PencilIcon, TrashIcon, CalendarIcon, UserGroupIcon, PhotoIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { eventApi, businessApi, serviceApi, staffApi } from '../services/api';
import { Event, Business } from '../types';
import { Modal } from '../components/Modal';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface EventForm {
  businessId: string;
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  maxCapacity: number;
  serviceId: string;
  staffId: string;
}

export function Events() {
  const [events, setEvents] = useState<Event[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [services, setServices] = useState<any[]>([]);
  const [staff, setStaff] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [selectedBusiness, setSelectedBusiness] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);

  const { register, handleSubmit, reset, watch, formState: { errors, isSubmitting } } = useForm<EventForm>({
    defaultValues: { maxCapacity: 10, description: '', serviceId: '', staffId: '' },
  });

  const watchedBusiness = watch('businessId');

  useEffect(() => { fetchData(); }, []);

  useEffect(() => {
    if (watchedBusiness && watchedBusiness !== selectedBusiness) {
      setSelectedBusiness(watchedBusiness);
      loadServicesAndStaff(watchedBusiness);
    }
  }, [watchedBusiness]);

  const fetchData = async () => {
    try {
      const bRes = await businessApi.getMyBusinesses();
      const bList = bRes.data.data.businesses || [];
      setBusinesses(bList);
      if (bList.length > 0) {
        const eRes = await eventApi.getBusinessEvents(bList[0].id);
        setEvents(eRes.data.data.events || []);
        await loadServicesAndStaff(bList[0].id);
      }
    } catch { toast.error('Error cargando datos'); }
    finally { setIsLoading(false); }
  };

  const loadServicesAndStaff = async (businessId: string) => {
    try {
      const [sRes, stRes] = await Promise.all([
        serviceApi.getBusinessServices(businessId),
        staffApi.getBusinessStaff(businessId),
      ]);
      setServices(sRes.data.data.services || []);
      setStaff(stRes.data.data.staff || []);
    } catch {}
  };

  const handleImageChange = (file: File) => {
    if (file.size > 5 * 1024 * 1024) { toast.error('La imagen no puede superar 5MB'); return; }
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = e => setImagePreview(e.target?.result as string);
    reader.readAsDataURL(file);
  };

  const uploadEventImage = async (eventId: string): Promise<string | null> => {
    if (!imageFile) return null;
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('image', imageFile);
      const r = await axios.post(`${API_URL}/upload/event/${eventId}/image`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      return r.data.data.url;
    } catch { return null; }
    finally { setUploadingImage(false); }
  };

  const onSubmit = async (data: EventForm) => {
    try {
      const payload = {
        businessId: data.businessId,
        title: data.title,
        description: data.description || undefined,
        startTime: new Date(data.startTime).toISOString(),
        endTime: new Date(data.endTime).toISOString(),
        maxCapacity: Number(data.maxCapacity),
        serviceId: data.serviceId || undefined,
        staffId: data.staffId || undefined,
      };

      let eventId: string;
      if (editingEvent) {
        await eventApi.updateEvent(editingEvent.id, payload as any);
        eventId = editingEvent.id;
        toast.success('Evento actualizado');
      } else {
        const r = await eventApi.createEvent(payload as any);
        eventId = r.data.data.event.id;
        toast.success('Evento creado');
      }

      if (imageFile) await uploadEventImage(eventId);

      setIsModalOpen(false);
      setEditingEvent(null);
      setImageFile(null);
      setImagePreview(null);
      reset();
      fetchData();
    } catch { toast.error('Error al guardar el evento'); }
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setImagePreview((event as any).image || null);
    setImageFile(null);
    reset({
      businessId: event.businessId,
      title: event.title,
      description: event.description || '',
      startTime: new Date(event.startTime).toISOString().slice(0, 16),
      endTime: new Date(event.endTime).toISOString().slice(0, 16),
      maxCapacity: event.maxCapacity || 10,
      serviceId: (event as any).serviceId || '',
      staffId: (event as any).staffId || '',
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este evento?')) return;
    try {
      await eventApi.deleteEvent(id);
      toast.success('Evento eliminado');
      fetchData();
    } catch { toast.error('Error al eliminar'); }
  };

  const handleAddNew = () => {
    setEditingEvent(null);
    setImageFile(null);
    setImagePreview(null);
    reset({
      businessId: businesses[0]?.id || '',
      title: '', description: '',
      startTime: '', endTime: '',
      maxCapacity: 10, serviceId: '', staffId: '',
    });
    setIsModalOpen(true);
  };

  if (isLoading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <style>{`
        .ev-card-img { width: 100%; height: 160px; object-fit: cover; border-radius: 12px 12px 0 0; }
        .ev-card-no-img { width: 100%; height: 100px; background: linear-gradient(135deg, #1e293b, #334155); border-radius: 12px 12px 0 0; display: flex; align-items: center; justify-content: center; font-size: 2.5rem; }
        .img-upload-area { border: 2px dashed #e2e8f0; border-radius: 12px; padding: 24px; text-align: center; cursor: pointer; transition: all .2s; background: #f8fafc; }
        .img-upload-area:hover { border-color: #3b82f6; background: #eff6ff; }
        .img-preview { width: 100%; height: 180px; object-fit: cover; border-radius: 12px; margin-bottom: 8px; }
      `}</style>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Eventos</h1>
          <p className="text-secondary-500 text-sm mt-1">Publica clases, actividades y promociones para tus clientes</p>
        </div>
        <button onClick={handleAddNew} className="btn-primary flex items-center gap-2">
          <PlusIcon className="h-5 w-5" />Nuevo evento
        </button>
      </div>

      {events.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {events.map(event => {
            const img = (event as any).image;
            const d = new Date(event.startTime);
            return (
              <div key={event.id} className="card overflow-hidden" style={{ padding: 0 }}>
                {img
                  ? <img src={img} alt={event.title} className="ev-card-img" />
                  : <div className="ev-card-no-img">📅</div>
                }
                <div style={{ padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <h3 className="text-base font-bold text-secondary-900" style={{ flex: 1 }}>{event.title}</h3>
                    <span style={{ background: '#dbeafe', color: '#1d4ed8', fontSize: '.7rem', fontWeight: 700, padding: '3px 8px', borderRadius: 100, marginLeft: 8, flexShrink: 0 }}>
                      {event.maxCapacity} plazas
                    </span>
                  </div>
                  {event.description && (
                    <p className="text-sm text-secondary-500 mb-3" style={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {event.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.78rem', color: '#64748b' }}>
                      <CalendarIcon style={{ width: 14 }} />
                      {d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' })}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '.78rem', color: '#64748b' }}>
                      🕐 {d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Madrid' })}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button onClick={() => handleEdit(event)} className="btn-secondary text-sm flex-1 flex items-center justify-center gap-1">
                      <PencilIcon className="h-4 w-4" />Editar
                    </button>
                    <button onClick={() => handleDelete(event.id)} style={{ padding: '8px 12px', borderRadius: 8, border: 'none', background: '#fef2f2', color: '#ef4444', cursor: 'pointer', transition: 'background .2s' }}>
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <div style={{ fontSize: '4rem', marginBottom: 16 }}>📅</div>
          <p className="text-secondary-500 mb-2 font-medium">No tienes eventos publicados</p>
          <p className="text-secondary-400 text-sm mb-6">Crea tu primer evento para que tus clientes puedan inscribirse</p>
          <button onClick={handleAddNew} className="btn-primary inline-flex items-center gap-2">
            <PlusIcon className="h-5 w-5" />Crear primer evento
          </button>
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setEditingEvent(null); setImageFile(null); setImagePreview(null); }} title={editingEvent ? 'Editar evento' : 'Nuevo evento'} maxWidth="lg">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          {/* IMAGEN */}
          <div>
            <label className="label mb-2">Imagen del evento</label>
            {imagePreview ? (
              <div style={{ position: 'relative' }}>
                <img src={imagePreview} alt="preview" className="img-preview" />
                <button type="button" onClick={() => { setImagePreview(null); setImageFile(null); }}
                  style={{ position: 'absolute', top: 8, right: 8, background: 'rgba(239,68,68,.9)', color: 'white', border: 'none', borderRadius: '50%', width: 28, height: 28, cursor: 'pointer', fontSize: '.8rem' }}>
                  ✕
                </button>
                <button type="button" onClick={() => imageInputRef.current?.click()}
                  style={{ position: 'absolute', bottom: 16, right: 8, background: 'rgba(0,0,0,.6)', color: 'white', border: 'none', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', fontSize: '.75rem', fontWeight: 600 }}>
                  📷 Cambiar
                </button>
              </div>
            ) : (
              <div className="img-upload-area" onClick={() => imageInputRef.current?.click()}>
                <div style={{ fontSize: '2rem', marginBottom: 8 }}>🖼️</div>
                <p style={{ fontSize: '.85rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>Subir imagen del evento</p>
                <p style={{ fontSize: '.75rem', color: '#94a3b8' }}>JPG, PNG · Máx 5MB · Recomendado 1200×630px</p>
              </div>
            )}
            <input ref={imageInputRef} type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => e.target.files?.[0] && handleImageChange(e.target.files[0])} />
          </div>

          {!editingEvent && (
            <div>
              <label className="label">Negocio *</label>
              <select {...register('businessId', { required: true })} className="input">
                {businesses.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="label">Título del evento *</label>
            <input {...register('title', { required: 'El título es obligatorio' })} type="text" className="input" placeholder="Ej: Clase de yoga, Torneo de padel, Jornada open..." />
            {errors.title && <p className="text-sm text-red-600 mt-1">{errors.title.message}</p>}
          </div>

          <div>
            <label className="label">Descripción</label>
            <textarea {...register('description')} className="input" rows={3} placeholder="Describe el evento, qué incluye, qué traer..." />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Fecha y hora inicio *</label>
              <input {...register('startTime', { required: 'Obligatorio' })} type="datetime-local" className="input" />
              {errors.startTime && <p className="text-sm text-red-600 mt-1">{errors.startTime.message}</p>}
            </div>
            <div>
              <label className="label">Fecha y hora fin *</label>
              <input {...register('endTime', { required: 'Obligatorio' })} type="datetime-local" className="input" />
              {errors.endTime && <p className="text-sm text-red-600 mt-1">{errors.endTime.message}</p>}
            </div>
          </div>

          <div>
            <label className="label">Plazas máximas</label>
            <input {...register('maxCapacity', { valueAsNumber: true })} type="number" min={1} className="input" />
          </div>

          <div className="grid grid-cols-2 gap-4">
            {services.length > 0 && (
              <div>
                <label className="label">Servicio asociado</label>
                <select {...register('serviceId')} className="input">
                  <option value="">— Sin servicio —</option>
                  {services.map(s => <option key={s.id} value={s.id}>{s.name} ({s.price}€)</option>)}
                </select>
              </div>
            )}
            {staff.length > 0 && (
              <div>
                <label className="label">Personal asignado</label>
                <select {...register('staffId')} className="input">
                  <option value="">— Sin asignar —</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{s.firstName} {s.lastName}</option>)}
                </select>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-2">
            <button type="button" onClick={() => { setIsModalOpen(false); setEditingEvent(null); setImageFile(null); setImagePreview(null); }} className="btn-secondary">Cancelar</button>
            <button type="submit" disabled={isSubmitting || uploadingImage} className="btn-primary">
              {isSubmitting || uploadingImage ? 'Guardando...' : editingEvent ? 'Actualizar evento' : 'Publicar evento'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
